import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { nutritionAPI } from '../../services/api'

const FOOD_CATEGORIES = ['vegetables', 'fruits', 'grains', 'protein', 'dairy', 'fats_oils', 'sweets',
  'beverages', 'snacks', 'condiments', 'prepared_meals', 'other']
const REQUIRED = ['calories', 'protein_g', 'carbohydrates_g', 'fat_g']
const NUTRIENTS = [...REQUIRED, 'fiber_g']
const EMPTY = Object.fromEntries(NUTRIENTS.map((k) => [k, '']))

const round1 = (v) => (Number.isFinite(Number(v)) ? String(Math.round(Number(v) * 10) / 10) : '')

// Per-100 g values suggested by the existing text analyzer (the manual nutrition entry), for "100 g <name>".
async function suggestPer100g(name) {
  const today = new Date()
  const r = await nutritionAPI.analyzeFoodText({
    description: `100 g ${name}`, mode: 'auto', meal_type: 'snack',
    meal_time: today.toISOString(), log_date: today.toISOString().slice(0, 10),
  })
  const total = r.data?.meal_total ?? {}
  const foods = r.data?.foods_identified ?? []
  const fiber = foods.reduce((sum, f) => sum + (Number(f.nutritional_summary?.fiber_g) || 0), 0)
  const values = { ...Object.fromEntries(REQUIRED.map((k) => [k, round1(total[k])])), fiber_g: fiber ? round1(fiber) : '' }
  if (!REQUIRED.some((k) => Number(values[k]) > 0)) throw new Error('empty suggestion')
  return values
}

// VH-229 refinement: create a food right from the plan mapping picker (values per 100 g) and hand it back.
export function NewFoodForm({ initialName = '', onCreated, onCancel }) {
  const { t } = useTranslation()
  const [name, setName] = useState(initialName)
  const [category, setCategory] = useState('other')
  const [values, setValues] = useState(EMPTY)
  const [error, setError] = useState(null)
  const asked = useRef(false)

  const suggest = useMutation({
    mutationFn: ({ foodName }) => suggestPer100g(foodName),
    // the automatic suggestion never overwrites what the user already typed; the explicit button does
    onSuccess: (suggested, { overwrite }) => setValues((prev) => (overwrite ? suggested
      : Object.fromEntries(NUTRIENTS.map((k) => [k, prev[k] === '' ? suggested[k] : prev[k]])))),
  })

  useEffect(() => {
    if (asked.current || initialName.trim().length < 2) return
    asked.current = true
    suggest.mutate({ foodName: initialName.trim(), overwrite: false })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- suggest once on open
  }, [])

  const create = useMutation({
    mutationFn: (payload) => nutritionAPI.createFood(payload).then((r) => r.data),
    onSuccess: (food) => onCreated?.(food),
  })

  const submit = (e) => {
    e.preventDefault()
    setError(null)
    if (!name.trim() || REQUIRED.some((k) => values[k] === '' || Number(values[k]) < 0)) {
      setError('required')
      return
    }
    if (values.fiber_g !== '' && Number(values.fiber_g) > Number(values.carbohydrates_g)) {
      setError('fiberTooHigh')
      return
    }
    create.mutate({
      name: name.trim(), category, serving_size: 100, serving_unit: 'g', data_source: 'user_manual',
      ...Object.fromEntries(NUTRIENTS.map((k) => [k, values[k] === '' ? 0 : Number(values[k])])),
    })
  }

  return (
    <form onSubmit={submit} className="mt-2 space-y-2 rounded border border-gray-300 bg-surface-container p-2" data-testid="new-food-form">
      <p className="text-sm font-semibold text-gray-900">{t('planMeals.bulk.newFood.title')}</p>
      <label className="block text-xs text-gray-700">
        {t('planMeals.bulk.newFood.name')}
        <input className="mt-0.5 w-full border rounded px-2 py-1 text-sm" value={name} maxLength={200}
          data-testid="new-food-name" onChange={(e) => setName(e.target.value)} />
      </label>
      {name.trim() !== initialName.trim() && (
        <p className="text-xs text-gray-600" data-testid="new-food-base-name-hint">{t('planMeals.bulk.newFood.baseNameHint')}</p>
      )}
      <p className="text-xs text-gray-600">{t('planMeals.bulk.newFood.per100g')}</p>
      {suggest.isPending && <p className="text-xs text-gray-600" role="status">{t('planMeals.bulk.newFood.suggesting')}</p>}
      {suggest.isSuccess && <p className="text-xs rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800" role="status"
        data-testid="new-food-suggested">{t('planMeals.bulk.newFood.suggested')}</p>}
      {suggest.isError && <p className="text-xs text-gray-600" role="status" data-testid="new-food-no-suggestion">
        {t('planMeals.bulk.newFood.noSuggestion')}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {NUTRIENTS.map((key) => (
          <label key={key} className="block text-xs text-gray-700">
            {t(`planMeals.bulk.newFood.${key}`)}{key === 'fiber_g' ? ` (${t('planMeals.bulk.newFood.optional')})` : ' *'}
            <input type="number" min="0" step="any" className="mt-0.5 w-full border rounded px-2 py-1 text-sm"
              data-testid={`new-food-${key}`} value={values[key]}
              onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))} />
          </label>
        ))}
      </div>
      <label className="block text-xs text-gray-700">
        {t('planMeals.bulk.newFood.category')}
        <select className="mt-0.5 w-full border rounded px-2 py-1 text-sm" value={category} data-testid="new-food-category"
          onChange={(e) => setCategory(e.target.value)}>
          {FOOD_CATEGORIES.map((c) => <option key={c} value={c}>{t(`foodCategories.${c}`)}</option>)}
        </select>
      </label>
      {error && <p className="text-xs text-red-700" role="alert">{t(`planMeals.bulk.newFood.${error}`)}</p>}
      {create.isError && <p className="text-xs text-red-700" role="alert">{t('planMeals.bulk.newFood.saveError')}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className="btn-primary text-sm" disabled={create.isPending} data-testid="new-food-save">
          {create.isPending ? t('planMeals.bulk.newFood.saving') : t('planMeals.bulk.newFood.save')}
        </button>
        <button type="button" className="text-sm underline" disabled={suggest.isPending || name.trim().length < 2}
          data-testid="new-food-suggest" onClick={() => suggest.mutate({ foodName: name.trim(), overwrite: true })}>
          {t('planMeals.bulk.newFood.suggest')}
        </button>
        <button type="button" className="text-sm underline" onClick={onCancel}>{t('mealPrep.actions.cancel')}</button>
      </div>
    </form>
  )
}

export default NewFoodForm
